/**
 * The `/markets` feed sort model — the single source of truth for the feed's
 * sort tabs.
 *
 * `markets-page.tsx` derives its `VALID_SORT_VALUES` guard from
 * `MARKETS_FEED_SORT_KEYS`, and the `/markets` SSR prefetch
 * (`@/lib/sdk/ssr/markets-listing`) derives its per-tab prefetch variants from
 * it — so the tab list is declared exactly once instead of being hand-synced
 * across three places.
 *
 * Plain (non-`'use client'`) module so the server-side SSR prefetch and the
 * client feed page can both import it.
 */

/**
 * The four real sort tabs in the `/markets` sort row. The presentational tab
 * row (`sortOptions` in `markets-page.tsx`) layers labels, icons, and the
 * virtual "Winners" pill on top of these keys.
 */
export const MARKETS_FEED_SORT_KEYS = [
  'trending',
  'tradingStartedAt',
  'endingSoon',
  'prizePool',
] as const;

/** A real `/markets` feed sort key. */
export type MarketsFeedSortKey = (typeof MARKETS_FEED_SORT_KEYS)[number];

/**
 * The default `/markets` tab. The feed page, its SSR prefetch, the header's
 * feed-intent prefetch, and the active-sort store all read this so their query
 * keys match on hydration.
 */
export const MARKETS_FEED_DEFAULT_SORT: MarketsFeedSortKey = 'trending';

/**
 * UI sort key → backend `sortOrder`. `endingSoon` ascends (soonest deadline
 * first); every other feed sort descends. Shared by the client feed query
 * (`markets-page.tsx`) and the SSR prefetch so the two produce the same
 * query key — a mismatch makes the prefetched cache miss and the grid flashes.
 */
export function getMarketsFeedSortOrder(sortBy: string): string {
  return sortBy === 'endingSoon' ? 'asc' : 'desc';
}
